import { zip } from 'fflate'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { listBlobs } from '~/storage/blobStore'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError, toStorageError } from '~/storage/errors'
import { asSchemaAgnostic, readAllStores, type StoreDump } from '~/storage/readAll'
import { STORE } from '~/storage/schema'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { sha256OfBlob, sha256OfText } from './checksum'
import {
  BACKUP_FORMAT,
  type BackupManifest,
  type BackupPayload,
  type BlobManifestEntry,
  backupFileName,
  blobPath,
  CURRENT_BACKUP_FORMAT_VERSION,
  DATA_FILE,
  MANIFEST_FILE,
  type SegmentEntry,
} from './format'
import { classifySize, DEFAULT_SIZE_POLICY, type SizePolicy } from './sizePolicy'

/**
 * Create a full recovery backup (D-030).
 *
 * Always complete: all app data, photos, and attachments. No privacy toggles.
 * Media is never dropped to save space — a large library is segmented instead.
 */

export interface BackupFile {
  readonly fileName: string
  readonly bytes: Uint8Array
  readonly segment: SegmentEntry | undefined
}

export interface CreatedBackup {
  readonly manifest: BackupManifest
  readonly files: readonly BackupFile[]
  readonly totalBytes: number
  readonly warning?: string
}

export interface CreateBackupOptions {
  readonly appVersion: string
  readonly locale?: string
  readonly policy?: SizePolicy
}

/** Promise wrapper over fflate's async zip, which uses a worker when available. */
function zipAsync(files: Record<string, [Uint8Array, { level: 0 | 6 }]>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(files, (error, data) => (error ? reject(error) : resolve(data)))
  })
}

/**
 * The one store a complete backup deliberately leaves behind (D-091, D-095 §1).
 *
 * **`connections` holds the Worker token and nothing else may hold a secret.**
 * D-030 says a backup contains all app data; this is the single, recorded
 * carve-out, and it exists because a backup file is a thing the user copies to
 * a cloud drive, mails to themselves, or hands to someone helping them recover
 * — all of which are fine for health records they chose to export and none of
 * which are fine for a live credential.
 *
 * **The store's key stays, with an empty array.** `validateBackup` checks the
 * payload against the current schema, where a *missing* store is a gap rather
 * than an empty one, so dropping the key entirely would make every backup
 * invalid. `payloadMigrations/v3.ts` already assumed this shape and said so.
 *
 * **`containsAllData` stays `true`, and that is not a lie.** The flag's contract
 * is "this is a recovery backup, not a weekly export" (D-030) — it is what stops
 * a partial artifact being restored — and that meaning is untouched. The
 * completeness it declares is over the user's records, which is what a restore
 * rebuilds.
 *
 * **Restoring does not bring the token back, on any device.** A restore clears
 * every store and rewrites from the payload, so the field is empty afterwards
 * even on the machine that made the backup. D-095 §1 accepts that and requires
 * the screen to say so; `SettingsPage` does.
 *
 * Until C7.0 this exclusion existed only as a sentence in `schema.ts`. Nothing
 * implemented it, and it was true purely because nothing ever wrote a record —
 * `readAllStores` enumerates `objectStoreNames` and skips only `blobs`, which is
 * the design that carries a new store automatically and carried this one too.
 * The guard and the first writer arrived together.
 */
function withoutCredentials(stores: StoreDump): StoreDump {
  /* Only rewrite the key when it is actually present. A database opened at an
     older version has no `connections` store, and inventing the key here would
     put a store in the payload that the schema of that version does not have. */
  if (!(STORE.connections in stores)) return stores

  return { ...stores, [STORE.connections]: [] }
}

export async function createBackup(
  db: SolaDatabase,
  now: Date,
  options: CreateBackupOptions,
): Promise<Result<CreatedBackup, StorageError>> {
  const policy = options.policy ?? DEFAULT_SIZE_POLICY

  try {
    // Read everything BEFORE any zipping or hashing. `readAllStores` uses one
    // short read-only transaction per store, so no transaction is ever open
    // across the async work below (D-028).
    const read = await readAllStores(asSchemaAgnostic(db), { includeBlobs: false })
    const structured = withoutCredentials(read)
    const blobs = await listBlobs(db)
    if (!blobs.ok) return blobs

    const payload: BackupPayload = { dbSchemaVersion: DB_SCHEMA_VERSION, stores: structured }
    const dataText = JSON.stringify(payload)
    const dataBytes = new TextEncoder().encode(dataText)
    const dataSha256 = await sha256OfText(dataText)

    const { blobEntries, blobBytes, blobTotalBytes } = await collectBlobs(blobs.value)

    const recordCounts = Object.fromEntries(
      Object.entries(structured).map(([store, rows]) => [store, rows.length]),
    )
    recordCounts.blobs = blobs.value.length

    const estimated = dataBytes.byteLength + blobTotalBytes
    const verdict = classifySize(estimated, policy)

    const segments = planSegments(verdict, blobEntries, policy, now)
    const placed =
      segments === undefined ? blobEntries : assignSegments(blobEntries, segments, policy)

    const manifest: BackupManifest = {
      format: BACKUP_FORMAT,
      backupFormatVersion: CURRENT_BACKUP_FORMAT_VERSION,
      dbSchemaVersion: DB_SCHEMA_VERSION,
      appVersion: options.appVersion,
      createdAt: now.toISOString(),
      recordCounts,
      blobCount: blobs.value.length,
      blobTotalBytes,
      dataSha256,
      blobs: placed,
      units: { weight: 'kg', length: 'cm' },
      locale: options.locale ?? 'en',
      ...(segments === undefined ? {} : { segments }),
      containsAllData: true,
    }

    const files = await assembleFiles({
      manifest,
      dataBytes,
      placed,
      blobBytes,
      segments,
      now,
    })

    const totalBytes = files.reduce((sum, file) => sum + file.bytes.byteLength, 0)

    return ok({
      manifest,
      files,
      totalBytes,
      ...(verdict.kind === 'singleWithWarning' ? { warning: verdict.message } : {}),
    })
  } catch (cause) {
    if (cause instanceof Error && cause.name === 'ChecksumUnavailableError') {
      return err(storageError('unavailable', cause.message, cause))
    }
    return err(toStorageError(cause))
  }
}

/**
 * Read and hash every blob, ONE AT A TIME.
 *
 * `crypto.subtle.digest` has no streaming form, so this bounds the peak at the
 * largest single blob rather than the whole library (D-030).
 */
async function collectBlobs(stored: readonly { blobId: string; blob: Blob; mimeType: string }[]) {
  const blobEntries: BlobManifestEntry[] = []
  const blobBytes = new Map<string, Uint8Array>()
  let blobTotalBytes = 0

  for (const entry of stored) {
    const bytes = new Uint8Array(await entry.blob.arrayBuffer())
    blobEntries.push({
      blobId: entry.blobId,
      sha256: await sha256OfBlob(entry.blob),
      byteSize: bytes.byteLength,
      mimeType: entry.mimeType,
      path: blobPath(entry.blobId),
      segment: 1,
    })
    blobBytes.set(entry.blobId, bytes)
    blobTotalBytes += bytes.byteLength
  }

  return { blobEntries, blobBytes, blobTotalBytes }
}

interface AssembleArgs {
  manifest: BackupManifest
  dataBytes: Uint8Array
  placed: readonly BlobManifestEntry[]
  blobBytes: ReadonlyMap<string, Uint8Array>
  segments: readonly SegmentEntry[] | undefined
  now: Date
}

async function assembleFiles(args: AssembleArgs): Promise<BackupFile[]> {
  const manifestBytes = new TextEncoder().encode(JSON.stringify(args.manifest, null, 2))
  const files: BackupFile[] = []

  // Photos are already compressed; re-deflating costs CPU for nothing.
  const blobEntry = (entry: BlobManifestEntry): [Uint8Array, { level: 0 }] => [
    args.blobBytes.get(entry.blobId) as Uint8Array,
    { level: 0 },
  ]

  if (args.segments === undefined) {
    const entries: Record<string, [Uint8Array, { level: 0 | 6 }]> = {
      [MANIFEST_FILE]: [manifestBytes, { level: 6 }],
      [DATA_FILE]: [args.dataBytes, { level: 6 }],
    }
    for (const entry of args.placed) entries[entry.path] = blobEntry(entry)

    files.push({
      fileName: backupFileName(args.now),
      bytes: await zipAsync(entries),
      segment: undefined,
    })
    return files
  }

  for (const segment of args.segments) {
    // The manifest is repeated in every part so any single file can be
    // inspected, and so a missing part is detectable from whichever part the
    // user does have.
    const entries: Record<string, [Uint8Array, { level: 0 | 6 }]> = {
      [MANIFEST_FILE]: [manifestBytes, { level: 6 }],
    }
    if (segment.index === 1) entries[DATA_FILE] = [args.dataBytes, { level: 6 }]
    for (const entry of args.placed.filter((blob) => blob.segment === segment.index)) {
      entries[entry.path] = blobEntry(entry)
    }
    files.push({ fileName: segment.fileName, bytes: await zipAsync(entries), segment })
  }

  return files
}

function planSegments(
  verdict: ReturnType<typeof classifySize>,
  blobs: readonly BlobManifestEntry[],
  policy: SizePolicy,
  now: Date,
): readonly SegmentEntry[] | undefined {
  if (verdict.kind !== 'segmented') return undefined

  // Pack blobs greedily; the count follows from what actually fits rather than
  // from the estimate, so no part exceeds the target.
  let count = 1
  let used = 0
  for (const blob of blobs) {
    if (used > 0 && used + blob.byteSize > policy.segmentTargetBytes) {
      count += 1
      used = 0
    }
    used += blob.byteSize
  }

  return Array.from({ length: count }, (_, index) => ({
    index: index + 1,
    of: count,
    fileName: backupFileName(now, { index: index + 1, of: count, fileName: '' }),
  }))
}

function assignSegments(
  blobs: readonly BlobManifestEntry[],
  segments: readonly SegmentEntry[],
  policy: SizePolicy,
): readonly BlobManifestEntry[] {
  let current = 1
  let used = 0
  return blobs.map((blob) => {
    if (used > 0 && used + blob.byteSize > policy.segmentTargetBytes && current < segments.length) {
      current += 1
      used = 0
    }
    used += blob.byteSize
    return { ...blob, segment: current }
  })
}
